import React, { useEffect, useMemo, useState } from 'react'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Container,
    Divider,
    Grid,
    Slider,
    Typography
} from '@mui/material'
import Swal from "sweetalert2";

import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness'
import SaveIcon from '@mui/icons-material/Save'
import ReplayIcon from '@mui/icons-material/Replay'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import WbTwilightIcon from '@mui/icons-material/WbTwilight'

import { useDispatch, useSelector } from 'react-redux'

import { CustomTitle } from '../../../common/CustomTitle/CustomTitle'
import { GoBack } from '../../../common/GoBack/GoBack'

import {
    getDynamicLightingThresholdOptions,
    getDynamicLightingThresholdKey,
    getDynamicLightingThresholdValue
} from '../../../../helpers/dynamicLightingThresholds'

import { getDynamicProfileExampleMeasures } from '../../../../helpers/dynamicProfileExampleMeasures'
import { useTonightSunsetSunrise } from '../../../../hooks/useTonightSunsetSunrise'

import customTheme from '../../../../material-ui/theme'
import { startPanelsDynamicProfilesCreate, startPanelsDynamicProfilesUpdate } from '../../../../reducers/dynamicLighting/panelsDynamicProfilesActions'
import { CmSensorMeasuresChartWithThresholds } from '../../../common/Charts/CmSensorMeasuresChart/CmSensorMeasuresChartWithThresholds';
import { TextFieldStyled } from '../../../../material-ui/styledComponents/assignments/assignmentsStyledComponents';
import { useNavigate } from 'react-router';
const getDefaultThresholdKey = thresholdOptions => {
    const lowOption = thresholdOptions.find(option => option.key === 'low')

    if (lowOption) {
        return lowOption.key
    }

    return thresholdOptions[0]?.key ? thresholdOptions[0].key : ''
}

const getThresholdMarks = (thresholdOptions) => {
    return thresholdOptions.map((option, index) => ({
        value: index,
        label: option.label
    }))
}

const getThresholdIndexByKey = (key, thresholdOptions) => {
    const index = thresholdOptions.findIndex(option => (
        option.key === key
    ))

    return index !== -1 ? index : 0
}

const getThresholdKeyByIndex = (index, thresholdOptions) => {
    const option = thresholdOptions[index]

    return option ? option.key : getDefaultThresholdKey(thresholdOptions)
}
const getInitialForm = (profile, sunriseOptions, sunsetOptions) => {
    const defaultSunriseKey = getDefaultThresholdKey(sunriseOptions)
    const defaultSunsetKey = getDefaultThresholdKey(sunsetOptions)

    return {
        name: profile?.name ? profile.name : '',
        sunset_switch_on_threshold:
            profile?.sunset_switch_on_threshold !== undefined &&
                profile?.sunset_switch_on_threshold !== null
                ? getDynamicLightingThresholdKey(
                    profile.sunset_switch_on_threshold,
                    sunsetOptions
                )
                : defaultSunsetKey,
        sunrise_switch_on_threshold:
            profile?.sunrise_switch_on_threshold !== undefined &&
                profile?.sunrise_switch_on_threshold !== null
                ? getDynamicLightingThresholdKey(
                    profile.sunrise_switch_on_threshold,
                    sunriseOptions
                )
                : defaultSunriseKey,
        description: profile?.description ? profile.description : ''
    }
}

const ThresholdExampleChart = ({
    title,
    measures,
    centerOn,
    baseDate,
    startHour,
    endHour,
    yScaleMax = undefined
}) => {
    return (
        <Card
            variant='outlined'
            sx={{
                mb: 2
            }}
        >
            <CardContent
                sx={{
                    p: 1.5,
                    '&:last-child': {
                        pb: 1.5
                    }
                }}
            >
                <Typography
                    variant='subtitle2'
                    sx={{
                        mb: 1,
                        fontWeight: 600
                    }}
                >
                    {title}
                </Typography>

                <CmSensorMeasuresChartWithThresholds
                    showThresholds={true}
                    measures={measures}
                    startHour={startHour}
                    endHour={endHour}
                    centerOn={centerOn}
                    legend={false}
                    todaySunriseSunset
                    baseDate={baseDate}
                    pointGrid={false}
                    showGrid={true}
                    height={180}
                    legendScale={0.85}
                    yScaleMax={yScaleMax}
                />
            </CardContent>
        </Card>
    )
}

const ThresholdSliderCard = ({
    title,
    subtitle,
    icon,
    name,
    value,
    marks,
    thresholdOptions,
    onChange,
    centerOn,
    goodWeatherMeasures,
    badWeatherMeasures,
    baseDate,
    startHour,
    endHour,
    leftLabel,
    rightLabel
}) => {

    const selectedIndex = getThresholdIndexByKey(value, thresholdOptions)

    const handleSliderChange = (event, newValue) => {
        onChange({
            target: {
                name,
                value: getThresholdKeyByIndex(newValue, thresholdOptions)
            }
        })
    }

    return (
        <Card
            variant='outlined'
            sx={{
                height: '100%'
            }}
        >
            <CardHeader
                avatar={
                    <Avatar>
                        {icon}
                    </Avatar>
                }
                title={title}
                subheader={subtitle}
            />

            <Divider />

            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={6}>
                        <ThresholdExampleChart
                            title='Climatología favorable'
                            measures={goodWeatherMeasures}
                            centerOn={centerOn}
                            baseDate={baseDate}
                            startHour={startHour}
                            endHour={endHour}
                            yScaleMax={350}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={6}>
                        <ThresholdExampleChart
                            title='Climatología desfavorable'
                            measures={badWeatherMeasures}
                            centerOn={centerOn}
                            baseDate={baseDate}
                            startHour={startHour}
                            endHour={endHour}
                            yScaleMax={350}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                px: {
                                    xs: 1,
                                    md: 5
                                },
                                pt: 2,
                                pb: 1
                            }}
                        >

                            <Box
                                sx={{
                                    position: 'relative',
                                    height: 18,
                                    mb: 0.5
                                }}
                            >
                                <Typography
                                    variant='caption'
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 600,
                                        color: customTheme.palette.primary.main
                                    }}
                                >
                                    {leftLabel}
                                </Typography>

                                <Typography
                                    variant='caption'
                                    sx={{
                                        position: 'absolute',
                                        left: '100%',
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 600,
                                        color: customTheme.palette.primary.main
                                    }}
                                >
                                    {rightLabel}
                                </Typography>
                            </Box>

                            <Slider
                                value={selectedIndex}
                                min={0}
                                max={thresholdOptions.length - 1}
                                step={1}
                                marks={marks}
                                onChange={handleSliderChange}
                                valueLabelDisplay='off'
                                sx={{
                                    color: customTheme.palette.primary.main,
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: customTheme.palette.primary.main
                                    },
                                    '& .MuiSlider-track': {
                                        backgroundColor: customTheme.palette.primary.main
                                    },
                                    '& .MuiSlider-rail': {
                                        opacity: 0.35
                                    },
                                    '& .MuiSlider-markActive': {
                                        backgroundColor: customTheme.palette.primary.main
                                    },
                                    '& .MuiSlider-markLabel': {
                                        fontSize: 12
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export const DynamicProfileFormScreen = ({ variant = 'create' }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const active = useSelector(state => state.panelsDynamicProfiles.active)
    const activeContract = useSelector(state => state.contracts.activeContract)
    const cmAdaptativeThresholdsSunrise = useSelector(state => state.contracts.cmAdaptativeThresholdsSunrise)
    const cmAdaptativeThresholdsSunset = useSelector(state => state.contracts.cmAdaptativeThresholdsSunset)

    const { sunrise, sunset } = useTonightSunsetSunrise(true)

    const thresholdOptionsSunrise = useMemo(
        () => getDynamicLightingThresholdOptions(cmAdaptativeThresholdsSunrise),
        [cmAdaptativeThresholdsSunrise]
    )

    const thresholdOptionsSunset = useMemo(
        () => getDynamicLightingThresholdOptions(cmAdaptativeThresholdsSunset),
        [cmAdaptativeThresholdsSunset]
    )
    const exampleMeasures = useMemo(() => (
        getDynamicProfileExampleMeasures({
            sunrise,
            sunset
        })
    ), [sunrise, sunset])

    const [formValues, setFormValues] = useState(
        getInitialForm(active, thresholdOptionsSunrise, thresholdOptionsSunset)
    )
    const {
        name,
        sunset_switch_on_threshold,
        sunrise_switch_on_threshold,
        description
    } = formValues

    const thresholdMarksSunrise = getThresholdMarks(thresholdOptionsSunrise)
    const thresholdMarksSunset = getThresholdMarks(thresholdOptionsSunset)

    useEffect(() => {
        setFormValues(getInitialForm(active, thresholdOptionsSunrise, thresholdOptionsSunset))
    }, [active, thresholdOptionsSunrise, thresholdOptionsSunset])

    const handleInputChange = event => {
        const { name, value } = event.target

        setFormValues(values => ({
            ...values,
            [name]: value
        }))
    }
    const handleCreate = (profileData) => {
        Swal.fire({
            title: "¿Estás seguro?",
            text: "Los cambios realizados tardarán un tiempo variable en llevarse a cabo",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: customTheme.palette.primary.main,
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
        }).then(async (res) => {
            if (res.isConfirmed) {
                dispatch(startPanelsDynamicProfilesCreate(profileData))
                navigate(-2);
            }
        });
    }
    const handleUpdate = (profileData) => {
        Swal.fire({
            title: "¿Estás seguro?",
            text: "Los cambios realizados tardarán un tiempo variable en llevarse a cabo",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: customTheme.palette.primary.main,
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
        }).then(async (res) => {
            if (res.isConfirmed) {
                dispatch(startPanelsDynamicProfilesUpdate(profileData))
                navigate(-1);
            }
        });
    }
    const handleReset = () => {
        setFormValues(getInitialForm(active, thresholdOptionsSunrise, thresholdOptionsSunset))
    }

    const handleSubmit = event => {
        event.preventDefault()

        const profileToSave = {
            ...active,
            name,
            sunset_switch_on_threshold: getDynamicLightingThresholdValue(
                sunset_switch_on_threshold,
                thresholdOptionsSunset
            ),
            sunrise_switch_on_threshold: getDynamicLightingThresholdValue(
                sunrise_switch_on_threshold,
                thresholdOptionsSunrise
            ),
            description
        }

        if (variant === 'create') {
            delete profileToSave.id
            delete profileToSave.contract_id
            delete profileToSave.contract_name
            delete profileToSave.date
            delete profileToSave.status_flag
            handleCreate(profileToSave)

        }
        if (variant === 'update') {
            handleUpdate(profileToSave)
        }
    }

    if (!active) {
        return (
            <Container
                maxWidth={false}
                sx={{
                    p: 2
                }}
            >
                <Grid container spacing={1}>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: 'flex'
                        }}
                    >
                        <GoBack to='/dynamic-lighting/profiles' />

                        <CustomTitle
                            title={'Perfil de iluminación dinámica'}
                            icon={
                                <Avatar>
                                    <SettingsBrightnessIcon />
                                </Avatar>
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography>
                                    {'No hay ningún perfil seleccionado'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        )
    }

    return (
        <Container
            maxWidth={false}
            sx={{
                p: 2
            }}
        >
            <Grid container spacing={2} justifyContent='center'>
                <Grid
                    item
                    xs={12}
                    sx={{
                        display: 'flex'
                    }}
                >
                    <GoBack
                        to={
                            variant === 'create'
                                ? '/dynamic-lighting/profiles/panels'
                                : '/dynamic-lighting/profiles'
                        }
                    />

                    <CustomTitle
                        title={`${'Perfiles de iluminación dinámica'} ~ ${activeContract?.name ? activeContract.name : 'Contrato'}`}
                        icon={
                            <Avatar>
                                <SettingsBrightnessIcon />
                            </Avatar>
                        }
                    />
                </Grid>

                <Grid item xs={12} md={10}>
                    <form onSubmit={handleSubmit}>
                        <Card>
                            <CardHeader
                                avatar={
                                    <Avatar
                                        src={customTheme.devicesIcons.panel.avatar}
                                        sx={{
                                            borderRadius: 0
                                        }}
                                    />
                                }
                                title={
                                    variant === 'create'
                                        ? 'Crear perfil'
                                        : 'Editar perfil'
                                }
                            />

                            <Divider />

                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextFieldStyled
                                            fullWidth
                                            required
                                            inputProps={{ maxLength: 50 }}
                                            name='name'
                                            label={'Nombre del perfil'}
                                            value={name}
                                            onChange={handleInputChange}
                                            autoComplete='off'
                                            disabled={variant === 'update'}
                                            variant='standard'
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>

                                        <ThresholdSliderCard
                                            title={'Orto'}
                                            subtitle={'Configuración del umbral de encendido al amanecer'}
                                            icon={<WbSunnyIcon />}
                                            name='sunrise_switch_on_threshold'
                                            value={sunrise_switch_on_threshold}
                                            marks={thresholdMarksSunrise}
                                            thresholdOptions={thresholdOptionsSunrise}
                                            onChange={handleInputChange}
                                            centerOn='sunrise'
                                            goodWeatherMeasures={exampleMeasures.sunrise.goodWeather}
                                            badWeatherMeasures={exampleMeasures.sunrise.badWeather}
                                            baseDate={sunrise ? sunrise.toDate() : null}
                                            startHour={exampleMeasures.sunrise.range.startHour}
                                            endHour={exampleMeasures.sunrise.range.endHour}
                                            leftLabel='Modo ahorro'
                                            rightLabel='Modo confort'
                                        />

                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <ThresholdSliderCard
                                            title={'Ocaso'}
                                            subtitle={'Configuración del umbral de encendido al anochecer'}
                                            icon={<WbTwilightIcon />}
                                            name='sunset_switch_on_threshold'
                                            value={sunset_switch_on_threshold}
                                            marks={thresholdMarksSunset}
                                            thresholdOptions={thresholdOptionsSunset}
                                            onChange={handleInputChange}
                                            centerOn='sunset'
                                            goodWeatherMeasures={exampleMeasures.sunset.goodWeather}
                                            badWeatherMeasures={exampleMeasures.sunset.badWeather}
                                            baseDate={sunset ? sunset.toDate() : null}
                                            startHour={exampleMeasures.sunset.range.startHour}
                                            endHour={exampleMeasures.sunset.range.endHour}
                                            leftLabel='Modo ahorro'
                                            rightLabel='Modo confort'
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextFieldStyled
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            label={'Descripción'}
                                            name='description'
                                            value={description}
                                            onChange={handleInputChange}
                                            variant='outlined'
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>

                            <Divider />

                            <CardActions
                                sx={{
                                    gap: 1,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Button
                                    type='submit'
                                    variant='contained'
                                    startIcon={<SaveIcon />}
                                    sx={{
                                        backgroundColor: customTheme.palette.primary.main,
                                        color: customTheme.palette.secondary.main,
                                        '&:hover': {
                                            backgroundColor: customTheme.palette.primary.main,
                                            opacity: 0.85
                                        }
                                    }}
                                >
                                    {'Guardar perfil'}
                                </Button>

                                <Button
                                    type='button'
                                    variant='contained'
                                    startIcon={<ReplayIcon />}
                                    onClick={handleReset}
                                    sx={{
                                        backgroundColor: customTheme.palette.primary.main,
                                        color: customTheme.palette.secondary.main,
                                        '&:hover': {
                                            backgroundColor: customTheme.palette.primary.main,
                                            opacity: 0.85
                                        }
                                    }}
                                >
                                    {'Cancelar'}
                                </Button>
                            </CardActions>
                        </Card>
                    </form>
                </Grid>
            </Grid>
        </Container>
    )
}